import { NewsPanel } from './NewsPanel';
import { NoteList } from './NoteList';
import { PredictionPanel } from './PredictionPanel';
import { DateScopeFilter } from './DateScopeFilter';
import { useState } from 'react';

/**
 * 工作区 —— **当日新闻与当日笔记**。
 *
 * 改前是「新闻 | 笔记」并排（≥1280px）、下方纵向堆叠。问题有三：
 *  1. 笔记**没有日期入口**（scope 由 period + effectiveDate 推导，面板自身
 *     无法选日期），想换日期必须绕道复盘页点月历；
 *  2. 新闻有自己独立的 `todayISO()` 日期控件，与图表日期完全脱钩，
 *     点 K 线柱对新闻毫无影响；
 *  3. 并排时每栏只有半屏宽，笔记正文被压得很窄。
 *
 * 现改为：顶部**一个统一日期筛选器**（笔记与新闻共用同一个日期源）+
 * 下方 tab 切换。日期一致是这里的核心 —— 同一个"日期"曾有两套状态，
 * 很容易各看各的。
 *
 * 移动端尤其需要 tab：并排在窄屏会退化成上下两块，需要滚动很远才能对照。
 */
export function WorkZone() {
  const [tab, setTab] = useState<'note' | 'news' | 'predict'>('note');

  return (
    <div className="flex flex-col">
      <DateScopeFilter />

      <div className="flex shrink-0 items-center gap-1 border-b px-2" role="tablist" aria-label="工作区内容">
        <TabBtn on={tab === 'predict'} onClick={() => setTab('predict')} icon="◎" label="预测" />
        <TabBtn on={tab === 'note'} onClick={() => setTab('note')} icon="✎" label="笔记" />
        <TabBtn on={tab === 'news'} onClick={() => setTab('news')} icon="📰" label="新闻" />
      </div>

      {/*
       * tab 体固定高度：两个面板高度不同且都会变 —— 笔记恒定（约 421px），
       * 新闻随条数在 113~429px 之间浮动。切换会改变文档高度，浏览器随即把
       * scrollTop 钳回新的上限，页面就上下跳（滚得越靠下跳得越狠）。
       * 固定后文档高度恒定，钳制无从发生。
       * 60vh 与新闻列表原有上限一致；min-h 兜底窄屏。
       * 注意不能用 flex-1：它的 flex-basis:0 会覆盖 height，而 WorkZone 根容器
       * 是内容高度、真正的滚动容器是 main，整条 flex 链没有确定高度时
       * flex-1 撑不开也收不住（实测新闻面板会铺到 4400px）。
       */}
      <div className="flex h-[60vh] min-h-[380px] flex-col">
        {/* 只渲染当前 tab：两者都挂载会各自发请求、且日期筛选器的语义会分裂 */}
        {tab === 'predict' ? <PredictionPanel /> : tab === 'note' ? <NoteList /> : <NewsPanel />}
      </div>
    </div>
  );
}

function TabBtn({ on, onClick, icon, label }: {
  on: boolean; onClick: () => void; icon: string; label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onClick}
      className="flex items-center gap-1 border-b-2 px-2.5 py-1.5 text-[12px] transition-colors"
      style={{
        borderColor: on ? 'var(--accent)' : 'transparent',
        color: on ? 'var(--text-1)' : 'var(--text-3)',
      }}
    >
      <span aria-hidden>{icon}</span>{label}
    </button>
  );
}